import React, { useEffect, useState, useCallback } from 'react';
import {
  ButtonPrimary,
  ButtonSecondary,
  cssColorValue,
  semanticColors,
  spacer24,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import analytics from 'utils/client/analytics/analytics';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { Vertical } from 'utils/vertical';
import { useVerticalConfig } from 'providers/vertical';

interface FeedbackWidgetProps {
  articleIdentifier: string;
  ubi: UbiArticlePage;
}

const Container = styled.div<{ vertical?: Vertical }>`
  border-top: 2px solid ${cssColorValue(semanticColors.decorativeSubdued)};
  min-height: 140px;
  margin-block-start: ${spacer24};
  padding-block: ${spacer8};
  padding-inline: ${props => (props.vertical === Vertical.ARTISTS ? `0 ${spacer8}` : spacer8)};
`;

const FeedbackWidget = ({ articleIdentifier, ubi }: FeedbackWidgetProps) => {
  const { t } = useTranslation();
  const { verticalName } = useVerticalConfig();
  const [answered, setAnswered] = useState<string>();

  useEffect(() => {
    ubi.logArticleHelpfulImpression();
  }, [ubi]);

  const vote = useCallback(
    (helpful: boolean) => {
      analytics.trackEvent({
        category: 'article-feedback',
        action: helpful ? 'yes' : 'no',
        label: `/article/${articleIdentifier}/`,
        nonInteraction: true,
      });

      if (helpful) {
        ubi.logArticleHelpfulYes();
      } else {
        ubi.logArticleHelpfulNo();
      }

      setAnswered(helpful ? 'yes' : 'no');
    },
    [articleIdentifier, ubi],
  );

  const renderButtons = () => {
    if (answered) {
      return (
        <Type role="alert" as="p">
          {t('feedback_widget_thanks')}
        </Type>
      );
    }

    return (
      <>
        <ButtonSecondary buttonSize={ButtonPrimary.sm} onClick={() => vote(true)}>
          {t('yes')}
        </ButtonSecondary>{' '}
        <ButtonSecondary buttonSize={ButtonPrimary.sm} onClick={() => vote(false)}>
          {t('no')}
        </ButtonSecondary>
      </>
    );
  };

  return (
    <Container vertical={verticalName}>
      <Type as="p" paddingBottom="1em">
        <strong>{t('feedback_widget_was_this_helpful')}</strong>
      </Type>
      {renderButtons()}
    </Container>
  );
};

export default FeedbackWidget;
